/**
 * Beslutsläge Configuration
 * 
 * Central configuration for all calculator-specific Beslutsläge implementations.
 * This enables reusable Premium logic across different calculators.
 * 
 * FRAMEWORK PRINCIPLES:
 * 1. Common: Payment flow, state management, Stripe integration, unlock logic
 * 2. Calculator-specific: Inputs, result structure, premium content generation
 * 3. PDF: Template-based with calculator-specific data injection
 */

// ==========================================
// PRICE CONFIGURATION (Single Source of Truth)
// ==========================================
export const BESLUTSLAGE_PRICE = 149; // SEK
export const BESLUTSLAGE_CURRENCY = 'kr';

// ==========================================
// CALCULATOR CONFIGURATIONS
// ==========================================
export const calculatorConfigs = {
  // ----------------------------------------
  // FASTIGHETSVÄRDE (Property Value)
  // ----------------------------------------
  fastighetsvarde: {
    id: 'fastighetsvarde',
    productType: 'fastighetsvarde',
    name: {
      sv: 'Fastighetsvärde indikator',
      de: 'Immobilienwert-Indikator',
      en: 'Property Value Indicator'
    },
    icon: 'Home',
    // Input fields definition
    inputs: [
      { key: 'region', type: 'select', options: ['norrland', 'svealand', 'gotaland'] },
      { key: 'areal', type: 'number', min: 1 },
      { key: 'virkesvolym', type: 'number', min: 1 },
      { key: 'jaktvarde', type: 'number', optional: true }
    ],
    // Result structure
    resultKeys: ['midEstimate', 'lowEstimate', 'highEstimate', 'pricePerHa'],
    // Regional benchmarks for interpretation
    benchmarks: {
      norrland: 65000,
      svealand: 85000,
      gotaland: 110000
    },
    regionNames: {
      sv: { norrland: 'norra Sverige', svealand: 'mellersta Sverige', gotaland: 'södra Sverige' }
    },
    // Premium content generators (functions that take result + inputs)
    premiumGenerators: {
      lagesbild: true,
      osakerheter: true,
      beslutsbehov: true,
      nastaSteg: true,
      beslutsbrev: true
    }
  },

  // ----------------------------------------
  // AVVERKNING (Harvesting/Logging)
  // ----------------------------------------
  avverkning: {
    id: 'avverkning',
    productType: 'avverkning',
    name: {
      sv: 'Avverkningsintäkt kalkylator',
      de: 'Einschlagsertrag-Rechner',
      en: 'Harvesting Income Calculator'
    },
    icon: 'Axe',
    inputs: [
      { key: 'volym', type: 'number', min: 1, label: { sv: 'Volym (m³)', de: 'Volumen (m³)', en: 'Volume (m³)' } },
      { key: 'prisPerM3', type: 'number', min: 0, label: { sv: 'Pris per m³ (kr)', de: 'Preis pro m³ (kr)', en: 'Price per m³ (kr)' } },
      { key: 'avverkningskostnad', type: 'number', min: 0, label: { sv: 'Avverkningskostnad (kr/m³)', de: 'Einschlagskosten (kr/m³)', en: 'Harvesting cost (kr/m³)' } }
    ],
    resultKeys: ['bruttoIntakt', 'totalKostnad', 'nettoIntakt', 'marginal'],
    // Avverkning-specific benchmarks
    benchmarks: {
      typicalPrice: { min: 900, max: 1300 },
      typicalCost: { min: 300, max: 400 }
    },
    premiumGenerators: {
      lagesbild: true,
      osakerheter: true,
      beslutsbehov: true,
      nastaSteg: true,
      beslutsbrev: true
    }
  },

  // ----------------------------------------
  // GALLRING (Thinning)
  // ----------------------------------------
  gallring: {
    id: 'gallring',
    productType: 'gallring',
    name: {
      sv: 'Gallringskalkylator',
      de: 'Durchforstungsrechner',
      en: 'Thinning Calculator'
    },
    icon: 'TreeDeciduous',
    inputs: [
      { key: 'bestandsvolym', type: 'number', min: 50, label: { sv: 'Beståndsvolym (m³/ha)', de: 'Bestandsvolumen (m³/ha)', en: 'Stand volume (m³/ha)' } },
      { key: 'uttagProcent', type: 'slider', min: 15, max: 50, step: 5, label: { sv: 'Uttag (%)', de: 'Entnahme (%)', en: 'Extraction (%)' } },
      { key: 'prisPerM3', type: 'number', min: 0, label: { sv: 'Virkespris (kr/m³)', de: 'Holzpreis (kr/m³)', en: 'Timber price (kr/m³)' } },
      { key: 'gallringskostnad', type: 'number', min: 0, label: { sv: 'Gallringskostnad (kr/m³)', de: 'Durchforstungskosten (kr/m³)', en: 'Thinning cost (kr/m³)' } }
    ],
    resultKeys: ['uttagenVolym', 'kvarvarandeVolym', 'bruttoIntakt', 'kostnad', 'nettoIntakt'],
    benchmarks: {
      typicalExtraction: { min: 25, max: 35 },
      typicalPrice: { min: 600, max: 900 },
      typicalCost: { min: 250, max: 350 }
    },
    premiumGenerators: {
      lagesbild: true,
      osakerheter: true,
      beslutsbehov: true,
      nastaSteg: true,
      beslutsbrev: true
    }
  }
};

// ==========================================
// HELPER FUNCTIONS
// ==========================================

/**
 * Get calculator configuration by ID
 */
export const getCalculatorConfig = (calculatorId) => {
  return calculatorConfigs[calculatorId] || null;
};

/**
 * Get all calculator IDs with Beslutsläge support
 */
export const getBeslutslageCalculators = () => {
  return Object.keys(calculatorConfigs);
};

/**
 * Format price display
 */
export const formatPrice = (price = BESLUTSLAGE_PRICE, currency = BESLUTSLAGE_CURRENCY) => {
  return `${price} ${currency}`;
};

/**
 * Get product type for backend API
 */
export const getProductType = (calculatorId) => {
  const config = calculatorConfigs[calculatorId];
  return config ? config.productType : calculatorId;
};

export default {
  BESLUTSLAGE_PRICE,
  BESLUTSLAGE_CURRENCY,
  calculatorConfigs,
  getCalculatorConfig,
  getBeslutslageCalculators,
  formatPrice,
  getProductType
};
